import { createBrowserRouter, Navigate } from 'react-router-dom';
import Routes from './Routes.ts';
import LoginPage from '@/pages/LoginPage.tsx';
import SandboxPage from '@/pages/SandboxPage.tsx';
import { transformRouterConfig } from '@/router/transformRouter.tsx';
import RegisterPage from '@/pages/RegisterPage.tsx';
import Default from '@/layouts/Default/Default.tsx';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage.tsx';
import ResetPasswordPage from '@/pages/ResetPasswordPage.tsx';
import HomePage from '@/pages/HomePage.tsx';
import SimpleCenter from '@/layouts/SimpleCenter/SimpleCenter.tsx';
import ConfirmEmailPage from '@/pages/ConfirmEmailPage.tsx';
import PaymentSelectPage from '@/pages/PaymentSelectPage.tsx';
import CreditsPage from '@/pages/CreditsPage.tsx';
import DataTablePage from '@/pages/DataTablePage.tsx';
import InputsPage from '@/pages/InputsPage.tsx';
import { LayoutInternal } from '$/views/emails/layout.tsx';
import BrandedSimple from '@/layouts/BrandedSimple/BrandedSimple.tsx';
import PasswordChangedConfirmationPage from '@/pages/PasswordChangedConfirmationPage.tsx';
import EmailChangedConfirmationPage from '@/pages/EmailChangedConfirmationPage.tsx';
import OnboardingPage from '@/pages/OnboardingPage.tsx';
import AccountPage from '@/pages/AccountPage.tsx';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage.tsx';
import CommonIntake from '@/pages/CommonIntake.tsx';
import ClarityDiagnosticLayer from '@/pages/ClarityDiagnosticLayer.tsx';
import RecommendationEngine from '@/pages/RecommendationEngine.tsx';
import SessionArchivePage from '@/pages/SessionArchivePage.tsx';
import SessionDetailPage from '@/pages/SessionDetailPage.tsx';
import AdminLayout from '@/layouts/Admin/AdminLayout.tsx';
import AdminUsersPage from '@/pages/admin/AdminUsersPage.tsx';
import AdminOrganizationsPage from '@/pages/admin/AdminOrganizationsPage.tsx';
import AdminSessionsPage from '@/pages/admin/AdminSessionsPage.tsx';
import AdminSessionDetailPage from '@/pages/admin/AdminSessionDetailPage.tsx';
import AdminEscalationsPage from '@/pages/admin/AdminEscalationsPage.tsx';

const router = createBrowserRouter(
  transformRouterConfig([
    {
      path: '/',
      element: <Default.Layout />,
      middleware: ['loggedIn', 'requiresOnboardingComplete'],
      children: [
        {
          path: Routes.DASHBOARD,
          element: <HomePage />,
        },
        {
          path: Routes.SANDBOX,
          element: <SandboxPage />,
        },
        {
          path: Routes.DATA_TABLE,
          element: <DataTablePage />,
        },
        {
          path: Routes.INPUTS,
          element: <InputsPage />,
        },
        {
          path: Routes.ACCOUNT,
          element: <AccountPage />,
        },
        {
          path: Routes.SESSION_ARCHIVE,
          element: <SessionArchivePage />,
        },
        {
          path: Routes.SESSION_DETAIL,
          element: <SessionDetailPage />,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn', 'requiresOnboardingComplete'],
      children: [
        {
          path: Routes.DASHBOARD,
          element: <HomePage />,
        },
        {
          path: Routes.COMMON_INTAKE,
          element: <CommonIntake />,
        },
        {
          path: Routes.CLARITY_DIAGNOSTIC_LAYER,
          element: <ClarityDiagnosticLayer />,
        },
        {
          path: Routes.RECOMMENDATION_ENGINE,
          element: <RecommendationEngine />,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['notLoggedIn'],
      children: [
        {
          path: Routes.FORGOT_PASSWORD,
          element: <ForgotPasswordPage />,
        },
        {
          path: Routes.LOGIN,
          element: <LoginPage />,
        },
        {
          path: Routes.REGISTER,
          element: <RegisterPage />,
        },
        {
          path: Routes.CONFIRM_EMAIL,
          element: <ConfirmEmailPage />,
        },
      ],
    },
    // SimpleCenter layout
    {
      path: '/',
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.RESET_PASSWORD,
          element: <ResetPasswordPage />,
        },
      ],
    },
    {
      path: '/',
      element: <BrandedSimple />,
      children: [
        {
          path: Routes.PASSWORD_CHANGED_CONFIRMATION,
          element: <PasswordChangedConfirmationPage />,
        },
        {
          path: Routes.EMAIL_CHANGED_CONFIRMATION,
          element: <EmailChangedConfirmationPage />,
        },
      ],
    },
    // SimpleCenter logged in
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn'],
      children: [
        {
          path: Routes.PAYMENT_SELECT,
          element: <PaymentSelectPage />,
        },
        {
          path: Routes.CREDITS,
          element: <CreditsPage />,
        },
      ],
    },
    {
      path: '/',
      element: <SimpleCenter />,
      middleware: ['loggedIn', 'onboardingGate'],
      children: [
        {
          path: Routes.ONBOARDING,
          element: <OnboardingPage />,
        },
      ],
    },
    {
      path: '/',
      element: <AdminLayout />,
      middleware: ['isAdmin'],
      children: [
        {
          path: Routes.ADMIN,
          element: <AdminSessionsPage />,
        },
        {
          path: Routes.ADMIN_SESSION_DETAIL,
          element: <AdminSessionDetailPage />,
        },
        {
          path: Routes.ADMIN_USERS,
          element: <AdminUsersPage />,
        },
        {
          path: Routes.ADMIN_ORGANIZATIONS,
          element: <AdminOrganizationsPage />,
        },
        {
          path: Routes.ADMIN_ESCALATIONS,
          element: <AdminEscalationsPage />,
        },
      ],
    },
    {
      path: Routes.EMAIL_TEMPLATE,
      element: (
        <LayoutInternal
          appUrl={import.meta.env.VITE_APP_URL}
          appName={'zde'}
          logo={`${import.meta.env.VITE_APP_URL}/assets/logo.png`}
        >
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ab animi atque possimus, quaerat quasi tempore
          totam! Architecto assumenda cumque deleniti dolor expedita facilis illo minima nihil quidem soluta? Hic,
          laboriosam!
        </LayoutInternal>
      ),
    },
    {
      path: Routes.PRIVACY_POLICY,
      element: <PrivacyPolicyPage />,
    },
    {
      path: '*',
      element: <Navigate to={Routes.DASHBOARD} />,
    },
  ])
);

export default router;
